import useMap from '@/composables/scopedStores/map/useMap'
import { eventOverrideLayerId } from '@/config/layerIds'
import { SimpleGeoJSONFeature } from '@lifk/types'
import { featureCollection, union } from '@turf/turf'
import { Feature, FeatureCollection, MultiPolygon, Polygon } from 'geojson'
import {
  ExpressionSpecification,
  FilterSpecification,
  GeoJSONFeature,
  LayerSpecification,
  PointLike,
  SourceSpecification
} from 'mapbox-gl'

export const useMapboxLayerUtils = () => {
  const map = useMap()

  const addSource = (sourceId: string, source: SourceSpecification) => {
    if (map.getSource(sourceId)) return

    try {
      map.addSource(sourceId, source)
    } catch (e) {
      console.warn('Error adding source: ', sourceId, e)
    }
  }

  const removeSource = (sourceId: string) => {
    if (!map.getSource(sourceId)) return
    try {
      // Instead of forcing removal of all layers, check if any layers are still using this source
      const style = map.getStyle()
      if (!style?.layers) return

      const layersUsingSource = style.layers.filter(
        (layer) => layer.source === sourceId
      )
      if (layersUsingSource.length > 0) {
        console.warn('Failure to remove source- it is still in use by layers.')
        return
      }

      // If no layers are using this source, it's safe to remove
      map.removeSource(sourceId)
    } catch (e) {
      console.warn('Error removing source: ', sourceId, e)
    }
  }

  const addImage = async (imageId: string, imageUrl: string) => {
    return await new Promise<boolean>((resolve) => {
      if (map.hasImage(imageId)) {
        resolve(true)
        return
      }
      map.loadImage(imageUrl, (error, image) => {
        if (error || !image) {
          console.warn('Error adding image: ', imageId, error)
          resolve(false)
        } else {
          // This is an async callback, so we need to check if the image has been added
          //   by another thread while waiting for loadImage to complete.
          if (!map.hasImage(imageId)) {
            map.addImage(imageId, image)
          }
          resolve(true)
        }
      })
    })
  }

  const getLayoutProperty = (layer: string, propertyName: string) => {
    if (!map.getLayer(layer)) return

    try {
      return map.getLayoutProperty(layer, propertyName)
    } catch (e) {
      console.warn(
        `Error getting layout property ${propertyName} from layer ${layer}`,
        e
      )
    }
  }

  const setLayoutProperty = (
    layer: string,
    propertyName: string,
    value: ExpressionSpecification
  ) => {
    if (!map.getLayer(layer)) return

    try {
      map.setLayoutProperty(layer, propertyName, value)
    } catch (e) {
      console.warn(
        `Error setting layout property ${propertyName} from layer ${layer}`,
        e
      )
    }
  }

  /*
    Optional second argument `beforeIds` can be provided to
      specify the comparative z-index of the layer.

    beforeId argument definition from the docs:
    https://docs.mapbox.com/mapbox-gl-js/api/map/#map#addlayer
    The ID of an existing layer to insert the new layer before,
      resulting in the new layer appearing visually beneath the existing layer.

    beforeIds should be ordered lowest -> highest, ie [bottomLayer, midLayer, topLayer],
      and contain layer ids of layers that should appear over the new layer.
    The lowest z-index, currently rendered layer will be used in map.addLayer(x, here)
  */
  const addLayer = (layer: LayerSpecification, beforeIds?: string[]) => {
    if (!layer.source || !map.getSource(layer.source) || map.getLayer(layer.id))
      return
    try {
      // eventOverrideLayerId is always on top
      const allBeforeIds = beforeIds
        ? [...beforeIds, eventOverrideLayerId]
        : [eventOverrideLayerId]

      // Use lowest beforeId, if found
      let bottomLayerId: string | undefined
      for (const beforeId of allBeforeIds) {
        const beforeIdPresent = !!map.getLayer(beforeId)
        if (beforeIdPresent) {
          bottomLayerId = beforeId
          break
        }
      }

      map.addLayer(layer, bottomLayerId)
    } catch (e) {
      console.warn('Error adding layer: ', layer.id, e)
    }
  }

  const removeLayer = (layerId: string) => {
    if (!map.getLayer(layerId)) return
    try {
      map.removeLayer(layerId)
    } catch (e) {
      console.warn('Error removing layer: ', layerId, e)
    }
  }

  const setFilter = (
    layerId: string,
    newFilter: FilterSpecification | undefined
  ) => {
    if (!map.getLayer(layerId)) return
    try {
      map.setFilter(layerId, newFilter)
    } catch (e) {
      console.warn('Error updating layer filter: ', layerId, e)
    }
  }

  const setPaintProperty = (
    layerId: string,
    propertyName: string,
    value: any
  ) => {
    if (!map.getLayer(layerId)) return
    try {
      map.setPaintProperty(layerId, propertyName, value)
    } catch (e) {
      console.warn(
        'Error updating layer paint property: ',
        layerId,
        propertyName,
        e
      )
    }
  }

  const setFeatureState = (
    sourceId: string,
    featureId: string,
    featureState: Record<string, boolean>
  ) => {
    if (!map.getSource(sourceId)) return

    try {
      map.setFeatureState(
        {
          source: sourceId,
          id: featureId
        },
        featureState
      )
    } catch (e) {
      console.warn('Error setting feature state: ', featureId, e)
    }
  }

  const setObjectFeatureState = (
    feature: any,
    featureState: Record<string, boolean>
  ) => {
    if (!feature.id || !map.getSource(feature.source)) return

    try {
      map.setFeatureState(feature, featureState)
    } catch (e) {
      console.warn('Error setting feature state: ', feature.id, e)
    }
  }

  const queryRenderedFeatures = (
    mapboxLayerIds: string[],
    point?: PointLike
  ): GeoJSONFeature[] => {
    try {
      // filter out any layers that don't exist on the map
      const existingLayers = mapboxLayerIds.filter((mbLayer) =>
        map.getLayer(mbLayer)
      )
      if (!existingLayers.length) return []

      const options = { layers: existingLayers }
      if (point) {
        return map.queryRenderedFeatures(point, options)
      } else {
        return map.queryRenderedFeatures(options)
      }
    } catch (_) {
      console.warn('Error querying rendered features: ', mapboxLayerIds)
    }
    return []
  }

  const querySourceFeatures = (
    sourceId: string,
    sourceLayer: string,
    filter: ExpressionSpecification
  ): GeoJSONFeature[] => {
    try {
      if (!map.getSource(sourceId)) return []
      return map.querySourceFeatures(sourceId, { sourceLayer, filter })
    } catch (_) {
      console.warn('Error querying source features: ', sourceId)
    }
    return []
  }

  // Returns entire feature geometry, unlike queryRenderedFeatures that clips features on tile boundaries.
  const getFullFeatureGeom = (
    feature: SimpleGeoJSONFeature,
    options: {
      sourceId: string
      sourceLayer: string
      filter: ExpressionSpecification
    }[]
  ): Feature<Polygon | MultiPolygon> | null => {
    const matchedFeatures = options.flatMap(
      ({ sourceId, sourceLayer, filter }) =>
        querySourceFeatures(sourceId, sourceLayer, filter!)
    )
    const matchedPolyFeatures = matchedFeatures.filter(
      (feat) =>
        feat.geometry.type === 'MultiPolygon' ||
        feat.geometry.type === 'Polygon'
    ) as Feature<Polygon | MultiPolygon>[]
    if (!matchedPolyFeatures.length) {
      return feature as Feature<Polygon | MultiPolygon>
    }

    const featureColl = featureCollection(
      matchedPolyFeatures
    ) as FeatureCollection<Polygon | MultiPolygon>
    const singleFeature =
      featureColl.features.length > 1
        ? union(featureColl)
        : matchedPolyFeatures[0]
    if (singleFeature && feature.properties) {
      singleFeature.properties = feature.properties
    }
    return singleFeature
  }

  return {
    addSource,
    removeSource,
    addImage,
    getLayoutProperty,
    setLayoutProperty,
    addLayer,
    removeLayer,
    setFilter,
    setPaintProperty,
    setFeatureState,
    setObjectFeatureState,
    queryRenderedFeatures,
    getFullFeatureGeom
  }
}
